import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { useClickOutside } from '../../hooks/useClickOutside'
import { TmbControlsRow, TmbStratificationPill, TmbPillSelect } from '../../components/TMBControls'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { exportToPNG, exportToSVG, exportToCSV } from '../../utils/tmbExportUtils'
import { normalizeKey, safeNumber, findColumnIndex, findColumnIndexByRegex, parseMonthToValue, formatDateLabel, formatPercentLabel, COLUMN_CANDIDATES } from '../../utils/dataUtils'
import { MonthInfo, HUDDLE_COLORS, normalizeHuddleGroup, extractRspNumber, codeForQuestionName, generateMonthList, sortQuestions, initializeSliderValues, getSliderIndicesFromValues, TMB_SLIDER_KEY, extractActualMonthValues, checkHasDataForRange } from '../../utils/tmbUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import { applyLaunchDateFilterToZohoData } from '../../utils/tmbLaunchDates'
import '../../styles/shared/index.css'
import './DisagreementAcross.css'

interface QuestionMeta {
    name: string
    code: string
    fullText: string
    rspNum: number | null
}

interface ColumnIndices {
    monthIdx: number
    questionNameIdx: number
    questionLabelIdx: number
    stratIdx: number
    huddleIdx: number
    numeratorIdx: number
    denominatorIdx: number
}

const PAGE_HEADING_LINE_1 = 'Percent of Patients who "Strongly'
const PAGE_HEADING_LINE_2 = 'Disagree" to Select Questions by Huddle'
const MAX_SELECTED_QUESTIONS = 7

const DisagreementAcross: React.FC = () => {
    const { selectedHospitalIds, refreshKey } = useHospitalSelection()
    const { globalMonthList } = useTmbSlider()
    const [monthList, setMonthList] = useState<MonthInfo[]>([])
    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [rawData, setRawData] = useState<any>(null)

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0])
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange)
    const hasDataForRange = checkHasDataForRange(sliderRange[0], sliderRange[1], actualMonths)

    const [selectedQuestions, setSelectedQuestions] = usePersistedState<string[]>('disagreement_across_questions', [])
    const [stratification, setStratification] = usePersistedState<string>('disagreement_across_stratification', 'ExcludeNotSure')
    const [questionDropdownOpen, setQuestionDropdownOpen] = useState(false)

    const [questions, setQuestions] = useState<QuestionMeta[]>([])
    const [columnIndices, setColumnIndices] = useState<ColumnIndices | null>(null)

    const [tableData, setTableData] = useState<{
        columns: string[]
        yesPcts: (number | null)[]
        noPcts: (number | null)[]
        yesNums: (number | null)[]
        noNums: (number | null)[]
    }>({ columns: [], yesPcts: [], noPcts: [], yesNums: [], noNums: [] })

    const [hoverTooltip, setHoverTooltip] = useState<{ visible: boolean, x: number, y: number, content: React.ReactNode } | null>(null)
    const [isMobile, setIsMobile] = useState(false);
 
    const tableContainerRef = useRef<HTMLDivElement>(null)
    const unselectAllCheckboxRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (unselectAllCheckboxRef.current) {
            if (selectedQuestions.length === 0) {
                unselectAllCheckboxRef.current.checked = true
                unselectAllCheckboxRef.current.indeterminate = false
            } else {
                unselectAllCheckboxRef.current.checked = false
                unselectAllCheckboxRef.current.indeterminate = true
            }
        }
    }, [selectedQuestions])

    const parseColumnIndices = (columns: string[]): ColumnIndices => {
        return {
            monthIdx: findColumnIndex(columns, COLUMN_CANDIDATES.month),
            questionNameIdx: findColumnIndex(columns, COLUMN_CANDIDATES.questionName),
            questionLabelIdx: findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel),
            stratIdx: findColumnIndex(columns, COLUMN_CANDIDATES.stratification),
            huddleIdx: findColumnIndex(columns, COLUMN_CANDIDATES.huddle),
            numeratorIdx: findColumnIndex(columns, COLUMN_CANDIDATES.numerator),
            denominatorIdx: findColumnIndex(columns, COLUMN_CANDIDATES.denominator)
        }
    }

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setRawData(null)
            setError(null)
            setLoading(false)
            setInitialLoading(false)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)
        setError(null)

        const disagreementUrl = `/api/tmb/disagreement_single/${hospitalIdsParam}`
        const agreementUrl = `/api/tmb/agreement_single/${hospitalIdsParam}`

        Promise.all([
            fetch(disagreementUrl).then(res => {
                if (!res.ok) throw new Error('Failed to fetch data')
                return res.json()
            }),
            fetch(agreementUrl).then(res => {
                if (!res.ok) return null
                return res.json()
            }).catch(() => null)
        ])
            .then(([data, agreementData]) => {
                applyLaunchDateFilterToZohoData(data)
                const rows = data?.response?.result?.rows || []
                const columns = data?.response?.result?.column_order || []

                if (rows.length === 0) {
                    setError('No data available for selected hospital(s)')
                    setLoading(false)
                    setInitialLoading(false)
                    return
                }

                const indices = parseColumnIndices(columns)

                const agreementLabelMap = new Map<string, string>()
                if (agreementData) {
                    const agreeRows = agreementData?.response?.result?.rows || []
                    const agreeCols = agreementData?.response?.result?.column_order || []
                    if (agreeRows.length > 0 && agreeCols.length > 0) {
                        const agreeQnIdx = findColumnIndex(agreeCols, COLUMN_CANDIDATES.questionName) !== -1
                            ? findColumnIndex(agreeCols, COLUMN_CANDIDATES.questionName)
                            : findColumnIndexByRegex(agreeCols, [/questionname/])
                        const agreeQlIdx = findColumnIndex(agreeCols, COLUMN_CANDIDATES.questionLabel) !== -1
                            ? findColumnIndex(agreeCols, COLUMN_CANDIDATES.questionLabel)
                            : findColumnIndexByRegex(agreeCols, [/questionlabel/, /questiontext/, /question/, /itemtext/, /item/])

                        if (agreeQnIdx >= 0 && agreeQlIdx >= 0) {
                            agreeRows.forEach((row: any[]) => {
                                if (!row) return
                                const qName = String(row[agreeQnIdx] || '').trim()
                                const qLabel = String(row[agreeQlIdx] || '').trim()
                                if (!qName || !qLabel) return
                                const existing = agreementLabelMap.get(qName)
                                if (!existing || qLabel.length > existing.length) {
                                    agreementLabelMap.set(qName, qLabel)
                                }
                            })
                        }
                    }
                }

                if (agreementLabelMap.size > 0 && indices.questionNameIdx >= 0 && indices.questionLabelIdx >= 0) {
                    rows.forEach((row: any[]) => {
                        if (!row) return
                        const qName = String(row[indices.questionNameIdx] || '').trim()
                        const agreementLabel = agreementLabelMap.get(qName)
                        if (agreementLabel) {
                            const currentLabel = String(row[indices.questionLabelIdx] || '').trim()
                            if (agreementLabel.length > currentLabel.length) {
                                row[indices.questionLabelIdx] = agreementLabel
                            }
                        }
                    })
                }

                setRawData(data)
                setColumnIndices(indices)

                setActualMonths(extractActualMonthValues(rows, indices.monthIdx))
                const months = generateMonthList(rows, indices.monthIdx)
                setMonthList(months)

                if (months.length > 0) {
                    const newRange = initializeSliderValues(months, sliderRange, 12)
                    if (newRange[0] !== sliderRange[0] || newRange[1] !== sliderRange[1]) {
                        setSliderRange(newRange)
                    }
                }

                const itemsByName = new Map<string, QuestionMeta>()

                rows.forEach((r: any[]) => {
                    const name = indices.questionNameIdx >= 0 ? String(r[indices.questionNameIdx] || '').trim() : ''
                    const label = indices.questionLabelIdx >= 0 ? String(r[indices.questionLabelIdx] || '').trim() : ''
                    if (!name) return

                    if (!itemsByName.has(name)) {
                        const code = codeForQuestionName(name)
                        itemsByName.set(name, { name, code, fullText: label || name, rspNum: extractRspNumber(name) })
                    }
                })

                const questionList = sortQuestions(Array.from(itemsByName.values()))

                setQuestions(questionList)

                if (questionList.length > 0 && selectedQuestions.length === 0) {
                    setSelectedQuestions(questionList.slice(0, Math.min(MAX_SELECTED_QUESTIONS, questionList.length)).map(q => q.name))
                }

                setLoading(false)
                setInitialLoading(false)
            })
            .catch(err => {
                setError(err.message || 'Failed to load data')
                setLoading(false)
                setInitialLoading(false)
            })
    }, [selectedHospitalIds, refreshKey])

    useEffect(() => {
        if (!rawData || !columnIndices) {
            setTableData({ columns: [], yesPcts: [], noPcts: [], yesNums: [], noNums: [] })
            return
        }

        const rows = rawData?.response?.result?.rows || []
        if (rows.length === 0) return

        const { monthIdx, questionNameIdx, stratIdx, huddleIdx, numeratorIdx, denominatorIdx } = columnIndices

        let filteredRows = rows
        if (monthIdx !== -1 && monthList.length > 0) {
            const minMonthVal = sliderRange[0] || 0
            const maxMonthVal = sliderRange[1] || 0

            if (minMonthVal > 0 || maxMonthVal > 0) {
                filteredRows = filteredRows.filter((r: any[]) => {
                    const v = parseMonthToValue(r[monthIdx])
                    return v >= minMonthVal && v <= maxMonthVal
                })
            }
        }

        if (stratIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => {
                return normalizeKey(r[stratIdx]) === normalizeKey(stratification)
            })
        }

        if (selectedQuestions.length === 0) {
            setTableData({ columns: [], yesPcts: [], noPcts: [], yesNums: [], noNums: [] })
            return
        }

        const perQuestion = new Map<string, {
            'Yes Huddle': { num: number; denom: number; hasNum: boolean; hasDenom: boolean };
            'No Huddle': { num: number; denom: number; hasNum: boolean; hasDenom: boolean }
        }>()
        selectedQuestions.forEach(qn => {
            perQuestion.set(qn, {
                'Yes Huddle': { num: 0, denom: 0, hasNum: false, hasDenom: false },
                'No Huddle': { num: 0, denom: 0, hasNum: false, hasDenom: false }
            })
        })

        filteredRows.forEach((r: any[]) => {
            if (questionNameIdx === -1 || huddleIdx === -1) return
            const qn = String(r[questionNameIdx] || '').trim()
            if (!perQuestion.has(qn)) return
            const g = normalizeHuddleGroup(r[huddleIdx])
            if (g !== 'Yes Huddle' && g !== 'No Huddle') return

            const num = numeratorIdx >= 0 ? safeNumber(r[numeratorIdx]) : null
            const denom = denominatorIdx >= 0 ? safeNumber(r[denominatorIdx]) : null

            const bucket = perQuestion.get(qn)![g as 'Yes Huddle' | 'No Huddle']
            if (num !== null) { bucket.num += num; bucket.hasNum = true }
            if (denom !== null) { bucket.denom += denom; bucket.hasDenom = true }
        })

        const columns: string[] = []
        const yesPcts: (number | null)[] = []
        const noPcts: (number | null)[] = []
        const yesNums: (number | null)[] = []
        const noNums: (number | null)[] = []

        selectedQuestions.forEach(qn => {
            const meta = questions.find(q => q.name === qn)
            columns.push(meta?.fullText || qn)

            const bucket = perQuestion.get(qn)
            const yes = bucket?.['Yes Huddle']
            const no = bucket?.['No Huddle']

            yesNums.push(yes?.hasDenom ? yes.denom : null)
            noNums.push(no?.hasDenom ? no.denom : null)

            let yesPct: number | null = null
            if (yes && yes.hasNum && yes.hasDenom && yes.denom > 0) yesPct = (yes.num / yes.denom) * 100
            let noPct: number | null = null
            if (no && no.hasNum && no.hasDenom && no.denom > 0) noPct = (no.num / no.denom) * 100

            yesPcts.push(yesPct)
            noPcts.push(noPct)
        })

        setTableData({ columns, yesPcts, noPcts, yesNums, noNums })
    }, [rawData, columnIndices, monthList, sliderRange, selectedQuestions, stratification, questions])
    useEffect(() => {
        const checkScreenSize = () => {
        setIsMobile(window.innerWidth <= 768); 
        };

        checkScreenSize();

        window.addEventListener("resize", checkScreenSize);

        return () => {
        window.removeEventListener("resize", checkScreenSize);
        };
    }, []);
    const handleQuestionToggle = (questionName: string) => {
        setSelectedQuestions(prev => {
            if (prev.includes(questionName)) {
                return prev.filter(q => q !== questionName)
            } else {
                if (prev.length >= MAX_SELECTED_QUESTIONS) return prev
                return [...prev, questionName]
            }
        })
    }

    const handleUnselectAll = () => {
        setSelectedQuestions([])
    }

    const getQuestionDisplayText = () => {
        if (selectedQuestions.length === 0) return 'Select'
        const codes = selectedQuestions.map(name => {
            const meta = questions.find(q => q.name === name)
            return meta?.code || ''
        }).filter(Boolean)
        if (codes.length === 1) return codes[0]
        if (codes.length === 2) return `${codes[0]}, ${codes[1]}`
        return `${codes.slice(0, 2).join(', ')} +${codes.length - 2}`
    }

    const handleDownloadPNG = async () => {
        if (!tableContainerRef.current) return
        await exportToPNG(tableContainerRef.current, 'Disagreement_Across_Questions',
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}` },
            { cssContextClass: 'disagreement-across-page', scale: 3 }
        )
    }

    const handleDownloadCSV = () => {
        const headers = ['page', 'hospital_ids', 'stratification', 'stratification_label', 'date_start', 'date_end', 'question_code', 'question_text', 'huddle_group', 'numerator', 'denominator', 'percent']
        const rows: (string | number)[][] = []

        const dateStart = globalMonthList[sliderStartIdx]?.label || ''
        const dateEnd = globalMonthList[sliderEndIdx]?.label || ''

        const dateStartFormatted = formatDateLabel(dateStart)
        const dateEndFormatted = formatDateLabel(dateEnd)

        const stratLabel = stratification === 'ExcludeNotSure' ? 'Exclude "Not"' : 'Include "Not"'

        const hospitalIds = Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join(',') : selectedHospitalIds

        selectedQuestions.forEach((qn, idx) => {
            const meta = questions.find(q => q.name === qn)
            const questionCode = meta?.code || `Q${idx + 1}`
            const questionText = meta?.fullText || qn

            const yesPct = tableData.yesPcts[idx]
            const noPct = tableData.noPcts[idx]
            const yesN = tableData.yesNums[idx]
            const noN = tableData.noNums[idx]

            const yesNum = yesN != null && yesPct != null ? Math.round((yesPct / 100) * yesN) : 0
            const noNum = noN != null && noPct != null ? Math.round((noPct / 100) * noN) : 0

            rows.push([
                'disagreement_across', hospitalIds, stratification, stratLabel,
                dateStartFormatted, dateEndFormatted, questionCode, questionText,
                'Yes Huddle', yesNum, yesN ?? 0, yesPct != null ? formatPercentLabel(yesPct) : ''
            ])

            rows.push([
                'disagreement_across', hospitalIds, stratification, stratLabel,
                dateStartFormatted, dateEndFormatted, questionCode, questionText,
                'No Huddle', noNum, noN ?? 0, noPct != null ? formatPercentLabel(noPct) : ''
            ])
        })

        exportToCSV({ headers, rows, filename: 'Disagreement_Across_Questions' })
    }

    const handleDownloadSVG = async () => {
        if (!tableContainerRef.current) return
        await exportToSVG(tableContainerRef.current, 'Disagreement_Across_Questions',
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}` },
            { cssContextClass: 'disagreement-across-page' }
        )
    }

    const [menuOpen, setMenuOpen] = useState(false)
    const downloadMenuRef = useRef<HTMLDivElement>(null)

    useClickOutside([downloadMenuRef], () => setMenuOpen(false), menuOpen)

    if (initialLoading) {
        return <LoadingNotice title="Loading disagreement data..." />
    }

    if (error) {
        return (
            <div className="disagreement-across-page tmb-page">
                <div className="content-wrapper">
                    <div className="header-wrapper">
                        <span className="page-title title--fixed">
                            <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                            <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                        </span>
                    </div>
                    <div className="section-card" style={{ minHeight: 'auto' }}>
                        {error.includes('No data available') ? (
                            <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                                No data available
                            </div>
                        ) : (
                            <p>Error: {error}</p>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    const maxBarHeight = 360

    return (
        <div id={generatePDFIds.dashboard()} className="disagreement-across-page tmb-page">
            <div className="content-wrapper">
                <div className="header-wrapper header--stratified">
                    <span className="page-title title--fixed">
                        <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                        <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                    </span>

                    <div className="controls-col controls--row">
                        <TmbControlsRow>
                            {globalMonthList.length > 0 && (
                                <DateRangeSlider
                                    monthList={globalMonthList}
                                    startIndex={sliderStartIdx}
                                    endIndex={sliderEndIdx}
                                    onChange={(start, end) => setSliderRange([globalMonthList[start]?.value || 0, globalMonthList[end]?.value || 0])}
                                />
                            )}

                            <TmbStratificationPill value={stratification} onChange={setStratification} />

                            <TmbPillSelect
                                displayText={getQuestionDisplayText()}
                                variant="question"
                                isOpen={questionDropdownOpen}
                                onToggle={setQuestionDropdownOpen}
                                infoTooltip={`Select up to ${MAX_SELECTED_QUESTIONS} questions`}
                            >
                                <div className="question-dd-inner">
                                    <div className="question-dd-meta">
                                        <span>{selectedQuestions.length}/{MAX_SELECTED_QUESTIONS} selected</span>
                                        {selectedQuestions.length >= MAX_SELECTED_QUESTIONS && (
                                            <span className="warn"> (max {MAX_SELECTED_QUESTIONS})</span>
                                        )}
                                    </div>
                                    <div
                                        className="question-dd-item unselect-all"
                                        onClick={handleUnselectAll}
                                    >
                                        <div className="q-row">
                                            <input
                                                type="checkbox"
                                                ref={unselectAllCheckboxRef}
                                                 onClick={handleUnselectAll}
                                            />
                                            <span className="q-text">Unselect All</span>
                                        </div>
                                    </div>
                                    {questions.map(q => {
                                        const isSelected = selectedQuestions.includes(q.name)
                                        const isDisabled = !isSelected && selectedQuestions.length >= MAX_SELECTED_QUESTIONS
                                        return (
                                            <div
                                                key={q.name}
                                                className={`question-dd-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                                onClick={() => !isDisabled && handleQuestionToggle(q.name)}
                                            >
                                                <div className="q-row">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => { }}
                                                        disabled={isDisabled}
                                                    />
                                                    <span className="q-text">{q.code} - {q.fullText}</span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </TmbPillSelect>
                        </TmbControlsRow>
                    </div>
                </div>

                <div className="section-card" ref={tableContainerRef} style={!hasDataForRange ? { minHeight: 'auto' } : undefined}>
                    {loading ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>Loading…</div>
                    ) : !tableData.columns.length || !hasDataForRange ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>No data available</div>
                    ) : (<>
                    <div className="custom-download-menu" ref={downloadMenuRef}>
                        <div className="hamburger-icon" onClick={() => setMenuOpen(!menuOpen)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <g>
                                    <rect x="4" y="6" width="16" height="2" rx="1" />
                                    <rect x="4" y="11" width="16" height="2" rx="1" />
                                    <rect x="4" y="16" width="16" height="2" rx="1" />
                                </g>
                            </svg>
                        </div>
                        {menuOpen && (
                            <div className="download-dropdown show">
                                <div className="menu-items-row">
                                    <div className="menu-item" onClick={async () => { await handleDownloadPNG(); setMenuOpen(false); }}>Download PNG</div>
                                    <div className="menu-item" onClick={async () => { await handleDownloadSVG(); setMenuOpen(false); }}>Download SVG</div>
                                    <div className="menu-item" onClick={() => { handleDownloadCSV(); setMenuOpen(false); }}>Download CSV</div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{ position: 'relative', marginTop:"20px" }}>
                        <div className="disagreement-across-table-wrap" id="disagreement-across-table-wrap">
                                <table className="disagreement-across-table">
                                    <colgroup>
                                        {tableData.columns.map((_, i) => (
                                            <col key={i} style={{ width: `${100 / tableData.columns.length}%` }} />
                                        ))}
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            {tableData.columns.map((col, i) => (
                                                <th key={i}>
                                                    <div className="q-head" title={col}>{col}</div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            {tableData.yesPcts.map((yesPct, i) => {
                                                const noPct = tableData.noPcts[i]
                                                const yesClamped = yesPct != null ? Math.max(0, Math.min(100, yesPct)) : 0
                                                const noClamped = noPct != null ? Math.max(0, Math.min(100, noPct)) : 0
                                                const yesHeight = Math.max(6, (yesClamped / 100) * maxBarHeight)
                                                const noHeight = Math.max(6, (noClamped / 100) * maxBarHeight)

                                                return (
                                                    <td key={i}>
                                                        <div className="bar-cell">
                                                            <div
                                                                className="bar-group"
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setHoverTooltip({
                                                                        visible: true,
                                                                        x: !isMobile? rect.right + 10: rect.right/2,
                                                                        y: rect.top + (rect.height / 2) - 30,
                                                                        content: (
                                                                            <div style={{ boxShadow: '2px 2px 6px -4px #999', cursor: 'default', zIndex: 9999, border: '1px solid #e3e3e3', backgroundColor: '#fff', minWidth: '150px', borderRadius: '5px', overflow: 'hidden' }}>
                                                                                <div className="apexcharts-tooltip-title" style={{ backgroundColor: '#ECEFF1', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', borderBottom: '1px solid #ddd', padding: '8px 10px', margin: 0, color: '#000' }}>
                                                                                    No Huddle {tableData.noNums[i] ?? 0}
                                                                                </div>
                                                                                <div style={{ padding: '8px 10px', backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                                                                    <span style={{ backgroundColor: HUDDLE_COLORS['No Huddle'], width: '10px', height: '10px', display: 'inline-block', borderRadius: '50%', marginRight: '8px' }}></span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginRight: '5px' }}>Strongly Disagree:</span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', fontWeight: 600, color: '#000' }}>
                                                                                        {formatPercentLabel(noClamped)}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    });
                                                                }}
                                                                onMouseLeave={() => setHoverTooltip(null)}
                                                            >
                                                                <div className="bar-value">{formatPercentLabel(noClamped)}</div>
                                                                <div
                                                                    className="bar-fill no-huddle"
                                                                    style={{ height: `${noHeight}px`, backgroundColor: HUDDLE_COLORS['No Huddle'] }}
                                                                />
                                                            </div>
                                                            <div
                                                                className="bar-group"
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setHoverTooltip({
                                                                        visible: true,
                                                                        x: !isMobile? rect.right + 10: rect.right/2,
                                                                        y: rect.top + (rect.height / 2) - 30,
                                                                        content: (
                                                                            <div style={{ boxShadow: '2px 2px 6px -4px #999', cursor: 'default', zIndex: 9999, border: '1px solid #e3e3e3', backgroundColor: '#fff', minWidth: '150px', borderRadius: '5px', overflow: 'hidden' }}>
                                                                                <div className="apexcharts-tooltip-title" style={{ backgroundColor: '#ECEFF1', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', borderBottom: '1px solid #ddd', padding: '8px 10px', margin: 0, color: '#000' }}>
                                                                                    Yes Huddle {tableData.yesNums[i] ?? 0}
                                                                                </div>
                                                                                <div style={{ padding: '8px 10px', backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                                                                    <span style={{ backgroundColor: HUDDLE_COLORS['Yes Huddle'], width: '10px', height: '10px', display: 'inline-block', borderRadius: '50%', marginRight: '8px' }}></span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginRight: '5px' }}>Strongly Disagree:</span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', fontWeight: 600, color: '#000' }}>
                                                                                        {formatPercentLabel(yesClamped)}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    });
                                                                }}
                                                                onMouseLeave={() => setHoverTooltip(null)}
                                                            >
                                                                <div className="bar-value">{formatPercentLabel(yesClamped)}</div>
                                                                <div
                                                                    className="bar-fill yes-huddle"
                                                                    style={{ height: `${yesHeight}px`, backgroundColor: HUDDLE_COLORS['Yes Huddle'] }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="n-row">
                                                            <span className="n-value">{tableData.noNums[i] != null ? Math.round(tableData.noNums[i]!) : 0}</span>
                                                            <span className="n-value">{tableData.yesNums[i] != null ? Math.round(tableData.yesNums[i]!) : 0}</span>
                                                        </div>
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    </tbody>
                                </table>
                        </div>
                        <div className="disagreement-across-legend" id="disagreement-across-legend">
                            <div className="legend-item">
                                <div className="legend-swatch no-huddle" />
                                <span>No Huddle</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-swatch yes-huddle" />
                                <span>Yes Huddle</span>
                            </div>
                        </div>
                    </div>
                    </>)}
                </div>
            </div>

            {
                hoverTooltip && hoverTooltip.visible && (
                    <div style={{
                        position: 'fixed',
                        top: hoverTooltip.y,
                        left: hoverTooltip.x,
                        zIndex: 10000,
                        pointerEvents: 'none',
                        transform: 'translateY(-50%)'
                    }}>
                        {hoverTooltip.content}
                    </div>
                )
            }
        </div >
    )
}

export default DisagreementAcross
